import { DesignLanguage, Flex, Heading, Text, useIsDesignLanguage } from '@afterpaytouch/core'
import { Trans, useTranslation, useUpdateAfterpayJsBrand } from '../../utils/convergence'
import React, { useEffect, useState } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { Country } from '@afterpaytouch/portal-api/types'
import { BackLink, Head, LoginPassword, Promotion } from '../../../src/component'
import { TermLink } from '../../../src/component/TermLink'
import { useShowReassuranceToast } from '../../../src/component/ReassuranceToast'
import { Route } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, Features, TerminalErrorCode } from '../../../src/model'
import {
  setCheckoutError,
  setMobileLoginVerifiedRoute,
  useAppDispatch,
  useAppRouter,
  useCountry,
  useIsCashApp,
  useMerchantId,
  useMobileLogin,
} from '../../../src/state'
import { useContinueAfterSignIn } from '../../../src/flow/sign-in/useContinueAfterSignIn'
import { markUsFebConsentCopyDisplayed } from '../../flow/one-offer-page/consentCopySignal'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { PromotionType } from '../../../src/component/Promotion/constants'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { CashSignInTerms } from '../../../src/component/CashSignInTerms'
import styles from './Password.module.css'

export const PasswordPage: CheckoutPage = () => {
  useUpdateAfterpayJsBrand()
  useShowReassuranceToast()
  const { t } = useTranslation()
  const router = useAppRouter()
  const { headingSize } = useCheckoutSizes()
  const { isMobileLogin, dateOfBirthVerificationRequired } = useMobileLogin()
  const merchantId = useMerchantId()
  const dispatch = useAppDispatch()
  const continueAfterSignIn = useContinueAfterSignIn()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isEvergreenConsentEnabled = useFlag<boolean>(Features.ENABLE_CHECKOUT_EVERGREEN_CONSENT, false)
  const country = useCountry()
  const [showUsFebConsent] = useState(() => isEvergreenConsentEnabled && country === Country.US)

  const pageTitle = `${t('password:welcomeBack')} | ${t('common:brandName')}`
  const isCashApp = useIsCashApp()

  useEffect(() => {
    if (showUsFebConsent) {
      markUsFebConsentCopyDisplayed()
    }
  }, [showUsFebConsent])

  useEffect(() => {
    router.prefetch(Route.ADD_MOBILE)
    router.prefetch(Route.ONE_TIME_PASSWORD)
    router.prefetch(Route.SIGN_UP)
    router.prefetch(Route.VERIFY)
    router.prefetch(Route.VERIFY_CHALLENGE)
    router.prefetch(Route.LOGGED_IN)
  }, [])

  const otpHandler = (): void => {
    if (isMobileLogin && dateOfBirthVerificationRequired) {
      dispatch(setMobileLoginVerifiedRoute(Route.ONE_TIME_PASSWORD))
      router.push(Route.VERIFY_CHALLENGE)
    } else {
      router.push(Route.ONE_TIME_PASSWORD)
    }
  }

  const declineHandler = (): void => {
    dispatch(
      setCheckoutError({
        error: TerminalErrorCode.AuthenticationDeclined,
        errorCode: ErrorCode.AuthenticationDeclined,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const forcePasswordResetRequiredHandler = (): void => {
    router.push(Route.PASSWORD_RESET_REQUIRED)
  }

  const content = (
    <>
      <Head title={pageTitle} />
      <BackLink backTo={Route.ENTER_EMAIL} />
      <Heading size={headingSize}>{t('password:welcomeBack')}</Heading>
      <LoginPassword
        onSuccess={continueAfterSignIn}
        onOTP={otpHandler}
        onRequires2FA={() => router.push(Route.VERIFY)}
        onDeclined={declineHandler}
        onForcePasswordResetRequired={forcePasswordResetRequiredHandler}
      />
      {isCashApp && <CashSignInTerms />}
      <Promotion merchantId={merchantId} promotionType={PromotionType.NEW_USER_PROMO} />
      <TrackPageViewed />
    </>
  )

  if (!showUsFebConsent) {
    return content
  }

  return (
    <Flex direction='column' justifyContent='space-between' alignItems='stretch' className={styles.bottomAnchor}>
      <div>{content}</div>
      <Text color='Gray40' size='XXS' fontFace='CashSansMono' testNameSpace='one-offer-page-consent'>
        <Trans
          i18nKey='login:oneOfferPageConsent'
          components={{
            feb: (
              <TermLink
                key='password-feb-privacy-policy'
                href={t('consumerLending:febPrivacyPolicyUrl')}
                color={isArcadeDesignLanguage ? 'Black' : 'Orca'}
                fontFace='CashSansMono'
                testNameSpace='one-offer-page-feb-privacy-policy'
                target='_blank'
                inline
              />
            ),
          }}
        />
      </Text>
    </Flex>
  )
}
